export const TITLE_OPTIONS = [
    { label: 'Mr', value: 'Mr' },
    { label: 'Mrs', value: 'Mrs' },
    { label: 'Ms', value: 'Ms' },
    { label: 'Dr', value: 'Dr' },
    { label: 'Prof', value: 'Prof' }
];

export const SUFFIX_OPTIONS = [
    { label: 'Sr', value: 'SR' },
    { label: 'Jr', value: 'JR' },
    { label: 'I', value: 'I' },
    { label: 'II', value: 'II' },
    { label: 'III', value: 'III' },
    { label: 'IV', value: 'IV' }
];

export const MARITAL_STATUS_OPTIONS = [
    { label: 'Single', value: 'Single' },
    { label: 'Married', value: 'Married' },
    { label: 'Divorced', value: 'Divorced' },
    { label: 'Widowed', value: 'Widowed' },
    { label: 'Unmarried', value: 'Unmarried' },
    { label: 'Separated', value: 'Separated' }
];

export const RELATIONSHIP_TO_THE_BUYER_OPTIONS = [
    { label: 'Spouse', value: 'Spouse' },
    { label: 'Relative', value: 'Relative' },
    { label: 'Registered Domestic Partner', value: 'RegisteredDomesticPartner' },
    { label: 'Civil Union', value: 'CivilUnion' },
    { label: 'Other', value: 'Other' }
];

export const CO_APPLICANT_TYPE_OPTIONS = [
    { label: 'Co-Signer', value: 'CoSigner' },
    { label: 'Guarantor', value: 'Guarantor' }
];

/**
 * Normalises suffix to uppercase 'SR' / 'JR' so SelectInput can match SUFFIX_OPTIONS values
 * regardless of how the value arrived from the API or Redux store.
 * Mirrors the transformSuffix utility in dash-components CustomerCardUtils.
 */
export const transformSuffix = (suffix?: string): string | undefined => {
    if (suffix?.toLowerCase() === 'sr') return 'SR';
    if (suffix?.toLowerCase() === 'jr') return 'JR';
    return suffix;
};

// Business-buyer support -----------------------------------------------------

export const BUSINESS = 'Business' as const;

// Dot-path field names fed to the validation engine (must match customerValidationConfig keys).
export const BUSINESS_LEGAL_NAME_FIELD = 'business.legalName';
export const BUSINESS_TAX_ID_NUMBER_FIELD = 'business.taxIdNumber';
export const BUSINESS_TYPE_FIELD = 'business.businessType';
export const BUSINESS_ESTABLISHED_DATE_FIELD = 'business.businessEstablishedDate';
export const BUSINESS_NUMBER_OF_EMPLOYEES_FIELD = 'business.numberOfEmployees';
export const BUSINESS_STATE_OF_INCORPORATION_FIELD = 'business.stateOfIncorporation';
export const BUSINESS_FINANCIAL_STATEMENT_DESCRIPTION_FIELD = 'business.financialStatementDescription';

export const BANK_NAME_FIELD = 'business.bankName';
export const BANK_CONTACT_NAME_FIELD = 'business.bankContactName';
export const BANK_CONTACT_PHONE_FIELD = 'business.bankContactPhoneNumber';
export const BANK_ACCOUNT_NUMBER_FIELD = 'bankAccountNumber';

export const TAX_ID_MASK = '*********';
export const CHECKING_ACCOUNT_NUMBER_MASK = '********************';

export const BUSINESS_TYPE_OPTIONS = [
    { label: 'Corporation', value: 'Corporation' },
    { label: 'LLC', value: 'LLC' },
    { label: 'Partnership', value: 'Partnership' },
    { label: 'Proprietor', value: 'Proprietor' },
    { label: 'Other', value: 'Other' }
];

export const FINANCIAL_STATEMENT_TYPE_OPTIONS = [
    { label: 'CPA-Audited', value: 'CPA-Audited' },
    { label: 'CPA-Reviewed', value: 'CPA-Reviewed' },
    { label: 'CPA-Compiled', value: 'CPA-Compiled' },
    { label: 'Income Tax', value: 'Income Tax' },
    { label: 'Borrower Prepared', value: 'Borrower Prepared' },
    { label: 'None', value: 'None' }
];

// Card operations ------------------------------------------------------------

/**
 * Operations that run *through* the card as a search → edit progression.
 * Swap Buyer/Co-Buyer and Drop Co-Buyer are direct operations — a host modal straight to a thunk —
 * and never set an operation.
 */
export const CARD_OPERATION_KIND = {
    CONVERT_TO_BUSINESS: 'convertToBusiness',
    CHANGE_BUYER: 'changeBuyer',
    CHANGE_CO_BUYER: 'changeCoBuyer',
    ADD_CUSTOMER: 'addCustomer',
    ADD_CO_BUYER: 'addCoBuyer'
} as const;

export const CARD_OPERATION_STEP = {
    SEARCH: 'search',
    EDIT: 'edit'
} as const;

/** Which participant's form an operation's draft seeds. A co-buyer can never be a business. */
export const CARD_OPERATION_TARGET = {
    [CARD_OPERATION_KIND.CONVERT_TO_BUSINESS]: 'buyer',
    [CARD_OPERATION_KIND.CHANGE_BUYER]: 'buyer',
    [CARD_OPERATION_KIND.ADD_CUSTOMER]: 'buyer',
    [CARD_OPERATION_KIND.CHANGE_CO_BUYER]: 'coBuyer',
    [CARD_OPERATION_KIND.ADD_CO_BUYER]: 'coBuyer'
} as const satisfies Record<(typeof CARD_OPERATION_KIND)[keyof typeof CARD_OPERATION_KIND], 'buyer' | 'coBuyer'>;

/** Whether leaving the operation closes the card, there being no participant behind it to return to. */
export const CARD_OPERATION_OWNS_CARD = {
    [CARD_OPERATION_KIND.CONVERT_TO_BUSINESS]: false,
    [CARD_OPERATION_KIND.CHANGE_BUYER]: false,
    [CARD_OPERATION_KIND.CHANGE_CO_BUYER]: false,
    [CARD_OPERATION_KIND.ADD_CUSTOMER]: true,
    [CARD_OPERATION_KIND.ADD_CO_BUYER]: true
} as const satisfies Record<(typeof CARD_OPERATION_KIND)[keyof typeof CARD_OPERATION_KIND], boolean>;

/** Whether an operation's results withhold CCIDs already on the deal. */
export const CARD_OPERATION_EXCLUDES_DEAL_PARTICIPANTS = {
    [CARD_OPERATION_KIND.CONVERT_TO_BUSINESS]: false,
    [CARD_OPERATION_KIND.CHANGE_BUYER]: true,
    [CARD_OPERATION_KIND.CHANGE_CO_BUYER]: true,
    [CARD_OPERATION_KIND.ADD_CUSTOMER]: true,
    [CARD_OPERATION_KIND.ADD_CO_BUYER]: true
} as const satisfies Record<(typeof CARD_OPERATION_KIND)[keyof typeof CARD_OPERATION_KIND], boolean>;

// Warning messages for unsaved edits before participant-replacement operations.
export const CARD_OPERATION_DISCARD_MESSAGE: Partial<
    Record<(typeof CARD_OPERATION_KIND)[keyof typeof CARD_OPERATION_KIND], string>
> = {
    [CARD_OPERATION_KIND.CHANGE_BUYER]:
        'You’ve made updates that haven’t been applied. Changing the buyer will discard your changes permanently. Do you want to keep them?',
    [CARD_OPERATION_KIND.CHANGE_CO_BUYER]:
        'You’ve made updates that haven’t been applied. Changing the co-buyer will discard your changes permanently. Do you want to keep them?',
    [CARD_OPERATION_KIND.CONVERT_TO_BUSINESS]:
        'You’ve made updates that haven’t been applied. Converting to business will discard your changes permanently. Do you want to keep them?'
};

export const CONTRACT_GENERATED_UNAVAILABLE_REASON = 'Not available after contracts are generated.';
